import * as i0 from '@angular/core';
import { InjectionToken, NgModule } from '@angular/core';
import { ApolloModule, APOLLO_OPTIONS } from 'apollo-angular';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { createUploadLink } from 'apollo-upload-client';
import { RetryLink } from '@apollo/client/link/retry';
import { __awaiter } from 'tslib';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Inject the options to construct the Apollo GraphQL module.
 */
const FRONT_GRAPHQL_OPTIONS = new InjectionToken('FRONT_GRAPHQL_OPTIONS');
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function createApollo(httpLink, options) {
    var _a, _b, _c;
    const link = ApolloLink.from([
        createSpecificRetryLink(),
        ...((_a = options.extraLinks) !== null && _a !== void 0 ? _a : []),
        createUploadLink({
            uri: options.uri,
            headers: { 'apollo-require-preflight': true },
        }),
    ]);
    return {
        link,
        cache: new InMemoryCache(options.cache),
        name: (_b = options.monitoring) === null || _b === void 0 ? void 0 : _b.name,
        version: (_c = options.monitoring) === null || _c === void 0 ? void 0 : _c.version,
    };
}
const createSpecificRetryLink = () => new RetryLink({
    delay: {
        initial: 300,
        max: 1000,
    },
    attempts: {
        max: 5,
        retryIf: (error, _operation) => !!error && _operation.operationName === 'GetTask',
    },
});
class FrontGraphQLModule {
}
FrontGraphQLModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FrontGraphQLModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FrontGraphQLModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FrontGraphQLModule, imports: [ApolloModule] });
FrontGraphQLModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FrontGraphQLModule, providers: [
        {
            provide: APOLLO_OPTIONS,
            useFactory: createApollo,
            deps: [HttpLink, FRONT_GRAPHQL_OPTIONS],
        },
    ], imports: [ApolloModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FrontGraphQLModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ApolloModule],
                    providers: [
                        {
                            provide: APOLLO_OPTIONS,
                            useFactory: createApollo,
                            deps: [HttpLink, FRONT_GRAPHQL_OPTIONS],
                        },
                    ],
                }]
        }] });

const AUTH_LINK_CONFIG_DEFAULT_VALUES = {
    header: 'Authorization',
    prefix: 'Bearer',
};
/**
 * Create a link adding the JWT token in the request header.
 *
 * @param config link config.
 */
const createAuthLink = (config) => {
    var _a, _b;
    const configWithDefaults = {
        header: (_a = config.header) !== null && _a !== void 0 ? _a : AUTH_LINK_CONFIG_DEFAULT_VALUES.header,
        prefix: (_b = config.prefix) !== null && _b !== void 0 ? _b : AUTH_LINK_CONFIG_DEFAULT_VALUES.prefix,
        tokenProvider: config.tokenProvider,
    };
    const prefix = configWithDefaults.prefix
        ? `${configWithDefaults.prefix} `
        : '';
    return setContext((operation, context) => __awaiter(void 0, void 0, void 0, function* () {
        const accessToken = yield configWithDefaults.tokenProvider();
        // getToken() returns "" when no token.
        if (accessToken === null) {
            return {};
        }
        else {
            return {
                headers: {
                    [configWithDefaults.header]: `${prefix}${accessToken}`,
                },
            };
        }
    }));
};

var GraphQLErrorType;
(function (GraphQLErrorType) {
    /**
     * A network error happened.
     */
    GraphQLErrorType[GraphQLErrorType["network"] = 0] = "network";
    /**
     * Unknown errors happened (not a network error)
     */
    GraphQLErrorType[GraphQLErrorType["exception"] = 1] = "exception";
    /**
     * Unknown errors happened (but still some data was fetched).
     */
    GraphQLErrorType[GraphQLErrorType["partialException"] = 2] = "partialException";
})(GraphQLErrorType || (GraphQLErrorType = {}));
/**
 * Create a link that notifies the app when errors occur.
 *
 * @param config the link config.
 */
const createGraphQLErrorNotificationLink = (config) => onError((error) => {
    var _a;
    let type;
    if (error.networkError != null) {
        type = GraphQLErrorType.network;
    }
    else if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.data) == null) {
        type = GraphQLErrorType.exception;
    }
    else {
        type = GraphQLErrorType.partialException;
    }
    config.onError(type);
});

const createUnauthenticatedLink = (func) => onError((error) => {
    var _a;
    (_a = error.graphQLErrors) === null || _a === void 0 ? void 0 : _a.forEach((elem) => {
        var _a, _b;
        // @ts-ignore
        if (((_b = (_a = elem.extensions) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status) === 401) {
            func();
        }
    });
});

/**
 * Generated bundle index. Do not edit.
 */

export { FRONT_GRAPHQL_OPTIONS, FrontGraphQLModule, GraphQLErrorType, createApollo, createAuthLink, createGraphQLErrorNotificationLink, createUnauthenticatedLink };
